'use client';

import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
    forwardRef,
    memo,
    PropsWithChildren,
    ReactNode,
    useCallback,
    useImperativeHandle,
    useState,
} from 'react';
import { Button } from '../ui/button';

export type DialogConfirmProps = {
    title: string;
    description?: string | ReactNode;

    defaultOpen?: boolean;

    onConfirm: (data?: any) => void | boolean | Promise<void | boolean>;
    onCancel?: VoidFunction;

    btnConfirmClassName?: string;
    btnCancelClassName?: string;

    hiddenCancel?: boolean;
};
export type DialogConfirmRefs = {
    showModal: (termData?: any) => void;
    closeModal: VoidFunction;
};

/**Modal dùng để xác nhận một hành động nào đó */
const DialogConfirm = forwardRef<
    DialogConfirmRefs,
    PropsWithChildren<DialogConfirmProps>
>((props, ref) => {
    const {
        title,
        onConfirm,
        onCancel: handleExtendCancel,
        description,
        children,
        btnConfirmClassName,
        btnCancelClassName,
        defaultOpen = false,
        hiddenCancel = false,
    } = props;

    const transDef = useTranslations('System');

    const [isShowDialog, setIsShowDialog] = useState(defaultOpen);
    const [termData, setTermData] = useState<any>();
    const [isLoading, setIsLoading] = useState(false);

    /**Hàm truyền vào component alert */
    const onOpenChange = useCallback(
        (open: boolean) => {
            if (isLoading) return;
            setIsShowDialog(open);
        },
        [isLoading]
    );

    /**Hàm mở modal */
    const onOpenModal = useCallback((term: any) => {
        setTermData(term);
        setIsShowDialog(true);
    }, []);

    /**Hàm click nút xác nhận trên modal */
    const onClickConfirm = useCallback(async () => {
        setIsLoading(true);
        const isClose = await onConfirm(termData);

        if (typeof isClose === 'boolean') {
            if (isClose) {
                setIsShowDialog(false);
            }
        } else {
            setIsShowDialog(false);
        }
        setIsLoading(false);
    }, [onConfirm, termData]);

    /**Hàm click nút hủy trên modal */
    const onCancel = useCallback(() => {
        setIsShowDialog(false);
        setTermData(undefined);
        handleExtendCancel?.();
    }, [handleExtendCancel]);

    useImperativeHandle(ref, () => {
        return {
            showModal: onOpenModal,
            closeModal: () => onOpenChange(false),
        };
    }, [onOpenChange, onOpenModal]);

    return (
        <AlertDialog open={isShowDialog} onOpenChange={onOpenChange}>
            {children && <AlertDialogTrigger>{children}</AlertDialogTrigger>}

            <AlertDialogContent
                onOpenAutoFocus={(e) => {
                    const target = e.target as HTMLDivElement;
                    target.querySelector('button')?.focus();
                }}
            >
                <AlertDialogHeader>
                    <AlertDialogTitle>{title}</AlertDialogTitle>
                    {description && (
                        <AlertDialogDescription>
                            {typeof description == 'string' ? (
                                <div
                                    className='whitespace-pre-wrap'
                                    dangerouslySetInnerHTML={{
                                        __html: description,
                                    }}
                                ></div>
                            ) : (
                                <div className='whitespace-pre-wrap'>
                                    {description}
                                </div>
                            )}
                        </AlertDialogDescription>
                    )}
                </AlertDialogHeader>
                <AlertDialogFooter>
                    {!hiddenCancel && (
                        <Button
                            variant='outline'
                            onClick={onCancel}
                            disabled={isLoading}
                            className={cn(
                                'flex items-center gap-x-1',
                                btnCancelClassName
                            )}
                        >
                            {transDef('System_Cancel')}
                        </Button>
                    )}

                    <Button
                        disabled={isLoading}
                        onClick={onClickConfirm}
                        className={cn(
                            'text-white: flex items-center gap-x-1 bg-red-600 hover:bg-red-400 hover:text-white',
                            btnConfirmClassName
                        )}
                    >
                        {isLoading && (
                            <Loader2 size={16} className='animate-spin' />
                        )}
                        {transDef('System_Confirm')}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
});

DialogConfirm.displayName = 'DialogConfirm';
export default memo(DialogConfirm);
